import React, { useCallback, useMemo } from "react";
import { styled, useTheme } from "@mui/material/styles";
import { Typography } from "@mui/material";
import ModelCardPicker from "../common/ModelCardPicker";
import {
  NODE_UI,
  stopPropagation,
  getCanvasSurfaceProps,
  getDialogSurfaceZIndex,
  inputSx,
} from "./nodeUiStyles";

/**
 * NodeModelPicker — model catalog picker for on-canvas node config.
 * Wraps ModelCardPicker with NodeUI label, propagation guards, and canvas menu positioning.
 */
const Label = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.label,
  fontWeight: NODE_UI.fontWeight.label,
  color: theme.palette.text.secondary,
  lineHeight: 1.4,
  marginBottom: "4px",
  userSelect: "none",
}));

const NodeModelPicker = React.memo(function NodeModelPicker({
  label = "Model",
  value,
  onChange,
  disabled = false,
  canvas = false,
  inDialog = false,
  includeLegacy = false,
  allowEmpty = false,
  placeholder = "Select a model...",
}) {
  const theme = useTheme();
  const canvasSurfaceProps = getCanvasSurfaceProps(canvas);

  const handleOpen = useCallback((event) => {
    event.stopPropagation();
  }, []);

  const menuProps = useMemo(
    () => ({
      disableScrollLock: canvas,
      ...(canvas ? { style: { zIndex: 1500 } } : {}),
      PaperProps: {
        ...canvasSurfaceProps,
        sx: (muiTheme) => ({
          borderRadius: NODE_UI.borderRadiusSm,
          boxShadow: NODE_UI.shadow,
          border: `1px solid ${muiTheme.palette.divider}`,
          marginTop: "2px",
          ...(inDialog ? { zIndex: getDialogSurfaceZIndex(muiTheme) } : {}),
        }),
      },
      MenuListProps: {
        onMouseDown: stopPropagation,
        onClick: stopPropagation,
        onPointerDown: stopPropagation,
      },
    }),
    [canvas, canvasSurfaceProps, inDialog]
  );

  return (
    <div
      className={canvas ? "nodrag nopan nowheel" : undefined}
      onMouseDown={stopPropagation}
      onClick={stopPropagation}
      onPointerDown={stopPropagation}
    >
      {label ? <Label component="label">{label}</Label> : null}
      <ModelCardPicker
        value={value}
        onChange={onChange}
        includeLegacy={includeLegacy}
        disabled={disabled}
        allowEmpty={allowEmpty}
        placeholder={placeholder}
        size="small"
        sx={{
          ...inputSx(theme),
          "& .model-card-picker__option-card--selected": {
            padding: "6px 28px 6px 0",
          },
          "& .model-card-picker__card-title": {
            fontSize: NODE_UI.fontSize.value,
          },
          "& .model-card-picker__card-description": {
            fontSize: NODE_UI.fontSize.caption,
          },
          "& .model-card-picker__legacy-toggle": {
            marginTop: "8px",
          },
        }}
        menuProps={menuProps}
        selectProps={{ onOpen: handleOpen }}
      />
    </div>
  );
});

export default NodeModelPicker;
